import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Form, Input, Button, Checkbox, Alert } from 'antd';
import { LockOutlined, UserOutlined, EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import { authService } from '../../services/authService';
import { consommerDestination } from '../../utils/retourApresConnexion';
import CBConnectLogo from '../../components/CBConnectLogo';
import CedeaoOrbit from '../../components/CedeaoOrbit';
import badgeCarteBrune from '../../assets/images/logo.png';

import SelecteurLangue from '../../components/SelecteurLangue';

/* ── Styles ─────────────────────────────────────────────────────────────────── */
const css = `
:root {
  --gd: #163d28; --gm: #1f5c3a; --gb: #1a6b3c; --gl: #3da068;
  --acc: #d5c93b; --tp: #0f1f17; --tm: #6b8077;
  --bd: #d4e2da; --wh: #ffffff; --bg: #f4f7f5;
}
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
.cb-page {
  font-family: 'DM Sans', sans-serif;
  min-height: calc(100vh - var(--cb-banniere-h, 0px)); display: flex; background: var(--bg);
}

/* ── Colonne gauche ── */
.cb-left {
  width: 46%; background: var(--gd); display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 52px 48px; position: relative; overflow: hidden;
}
.cb-ring {
  position: absolute; border-radius: 50%;
  border: 1px solid rgba(255,255,255,0.06); z-index: 1; pointer-events: none;
}
.cb-ring-1 { width: 500px; height: 500px; top: -220px; right: -200px; }
.cb-ring-2 { width: 300px; height: 300px; bottom: -80px; left: -120px; }
.cb-ring-3 {
  width: 160px; height: 160px; bottom: 160px; right: 60px;
  background: rgba(213,201,59,0.04); border-color: rgba(213,201,59,0.12);
}

.cb-left-inner {
  position: relative; z-index: 2;
  display: flex; flex-direction: column; align-items: center;
  width: 100%; text-align: center;
}

/* Hero logo - l'espacement est porté par l'anneau des drapeaux qui l'entoure */
.cb-hero-logo {
  filter: drop-shadow(0 8px 32px rgba(52,208,88,0.25));
}

/* Brand name */
.cb-brand-name {
  font-family: 'Syne', sans-serif;
  font-weight: 800; font-size: 28px; color: #fff;
  letter-spacing: -0.5px; line-height: 1.1;
  margin-bottom: 4px;
}
.cb-brand-sub {
  font-size: 13px; color: rgba(255,255,255,0.45);
  letter-spacing: 2px; text-transform: uppercase;
  margin-bottom: 40px;
}

/* Divider */
.cb-divider {
  width: 48px; height: 2px;
  background: linear-gradient(90deg, var(--acc), transparent);
  border-radius: 2px; margin: 0 auto 28px;
}

.cb-headline {
  /* En capitales et sur une seule ligne. 17px et non 20 : « LOGICIEL DE
     GESTION DES SINISTRES TRANSFRONTALIERS » fait pres de 50 signes, et les
     capitales sont plus larges - a 20px la phrase repassait a la ligne, ce
     qu'on cherchait justement a supprimer. Elle tient sur une ligne des
     ~560px de colonne ; en dessous elle se replie plutot que de deborder. */
  font-size: 17px; font-weight: 700; color: rgba(255,255,255,0.9);
  text-transform: uppercase; letter-spacing: 0.6px;
  line-height: 1.4; margin-bottom: 0;
}
.cb-headline span { color: var(--acc); }

.cb-footer-left {
  margin-top: 48px; font-size: 11px; color: rgba(255,255,255,0.25);
}

/* ── Colonne droite (formulaire) ── */
.cb-right {
  flex: 1; display: flex; align-items: center; justify-content: center;
  padding: 48px 60px; background: var(--bg);
}
.cb-form-wrap { width: 100%; max-width: 420px; }
.cb-form-eyebrow {
  font-size: 11px; font-weight: 600; letter-spacing: 2px;
  text-transform: uppercase; color: var(--gb); margin-bottom: 8px;
}
.cb-form-title  { font-size: 28px; font-weight: 700; color: var(--tp); margin-bottom: 6px; }
.cb-form-subtitle {
  font-size: 14px; color: var(--tm); margin-bottom: 36px; line-height: 1.5;
}

@media (max-width: 900px) {
  .cb-left { width: 42%; padding: 40px 32px; }
}
@media (max-width: 768px) {
  .cb-left { display: none; }
  .cb-right { padding: 32px 24px; }
}
`;

export default function Login() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // `remember` était collecté par le formulaire puis jeté ici : la case était
  // affichée sans qu'aucun mécanisme ne la serve.
  const onFinish = async ({ login, password, remember }) => {
    setError('');
    setLoading(true);
    try {
      const res = await authService.login(login, password, Boolean(remember));

      // Appareil non autorisé ou demande de validation déjà en cours : le mot de
      // passe était bon, mais aucune session ne s'ouvre. Le message vient du
      // serveur, qui seul sait laquelle des deux situations s'applique.
      if (res?.statut === 'APPAREIL_BLOQUE' || res?.statut === 'VALIDATION_ADMIN_EN_COURS') {
        setError(res.message || t('login.messages.appareilNonAutorise'));
        return;
      }

      if (res?.requires2FA) {
        toast.info(t('login.messages.codeEnvoye'));
        navigate('/otp-verify');
        return;
      }
      toast.success(t('login.messages.connexionReussie'));
      // Retour à la page visée avant la redirection vers la connexion - le message
      // pointé par un courriel de mention, typiquement. À défaut, le tableau de bord.
      navigate(consommerDestination());
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || t('login.messages.identifiantsIncorrects');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{css}</style>
      <SelecteurLangue variante="clair" flottant />
      <div className="cb-page">

        {/* ── Colonne gauche - hero ── */}
        <div className="cb-left">
          <div className="cb-ring cb-ring-1" />
          <div className="cb-ring cb-ring-2" />
          <div className="cb-ring cb-ring-3" />

          <div className="cb-left-inner">
            <CedeaoOrbit radius={185} duration={60}>
              {/* Le badge Carte Brune prend la place du logotype « CB ».
                  showLabel est desactive : l'arc « CARTE · BRUNE · CEDEAO »
                  redirait mot pour mot ce que le badge porte deja, en anneau
                  concentrique. */}
              <CBConnectLogo
                size={230}
                variant="color"
                showLabel={false}
                embleme={badgeCarteBrune}
                className="cb-hero-logo"
              />
            </CedeaoOrbit>

            <div className="cb-brand-name">CBConnect</div>
            <div className="cb-brand-sub">BNCB Togo</div>

            <div className="cb-divider" />

            <h1 className="cb-headline">
              <Trans t={t} i18nKey="login.accroche" components={{ 1: <span /> }} />
            </h1>
            <div className="cb-footer-left">
              © {new Date().getFullYear()} OSSAN ASUR SARL - BNCB Togo
            </div>
          </div>
        </div>

        {/* ── Colonne droite - formulaire ── */}
        <div className="cb-right">
          <div className="cb-form-wrap">
            <div className="cb-form-eyebrow">{t('login.surTitre')}</div>
            <h2 className="cb-form-title">{t('login.titre')}</h2>
            <p className="cb-form-subtitle">
              {t('login.sousTitre')}
            </p>

            {error && (
              <Alert
                type="error" message={error} showIcon
                style={{ marginBottom: 20, borderRadius: 8 }}
              />
            )}

            <Form layout="vertical" onFinish={onFinish} size="large">
              <Form.Item
                label={t('login.champs.identifiant')}
                name="login"
                rules={[{ required: true, message: t('login.champs.champRequis') }]}
              >
                <Input
                  prefix={<UserOutlined style={{ color: '#6b7280' }} />}
                  // Domaine neutre : le logiciel n'est pas lie au bureau
                  // togolais, un autre bureau CEDEAO l'installerait avec le
                  // sien. Aucun nom de pays dans un texte d'exemple.
                  placeholder={t('login.champs.exempleIdentifiant')}
                  style={{ borderRadius: 10, height: 46 }}
                />
              </Form.Item>

              <Form.Item
                label={t('login.champs.motDePasse')}
                name="password"
                rules={[{ required: true, message: t('login.champs.champRequis') }]}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: '#6b7280' }} />}
                  placeholder="••••••••"
                  iconRender={(v) => v ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                  style={{ borderRadius: 10, height: 46 }}
                />
              </Form.Item>

              <div style={{
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', marginBottom: 20,
              }}>
                <Form.Item name="remember" valuePropName="checked" noStyle>
                  <Checkbox style={{ fontSize: 13 }}>{t('login.seSouvenir')}</Checkbox>
                </Form.Item>
                <Link to="/mot-de-passe-oublie" style={{ fontSize: 13, color: '#1a6b3c' }}>
                  {t('login.motDePasseOublie')}
                </Link>
              </div>

              <Form.Item noStyle>
                <Button
                  type="primary" htmlType="submit" loading={loading}
                  style={{
                    width: '100%', height: 48, borderRadius: 10,
                    fontSize: 15, fontWeight: 600, background: '#1a6b3c',
                    border: 'none'
                  }}
                >
                  {loading ? t('login.connexionEnCours') : t('login.seConnecter')}
                </Button>
              </Form.Item>
            </Form>
          </div>
        </div>

      </div>
    </>
  );
}